import React from 'react';
import { resolveImageSrc, DEFAULT_AVATAR, avatarFallbackUrl } from '../utils/media';
import { Phone, Mail, Globe, Share2, Radio, MapPin, Linkedin, Twitter, Instagram, Facebook, Download, Music2, QrCode as QrCodeIcon, UserPlus } from 'lucide-react';
const QrCode = QrCodeIcon;
import UnifiedBrandIcon from './UnifiedBrandIcon';
import { User, AdConfig } from '../types';
import { QRCodeModal } from './QRCodeModal';
import { EnhancedEmailLeadForm } from './EnhancedEmailLeadForm';
import { BroadcastModal } from './BroadcastModal';
import BroadcastLoginModal from './BroadcastLoginModal';
import safeToast from '../utils/safeToast';
import { REMOTE_FALLBACK_COVER_URL } from '../constants/images';
import { DEFAULT_COVER } from '@/constants/media';
import { resolveUserColors } from '../utils/colors';
import { mockAds } from '../data/mockData';
import { trackAdView, trackAdClick } from '../api/analytics';
import { listLeadsByOwner } from '../api/leads';
import { canonicalCardUrl } from '@/utils/url';
import { useShare } from '@/context/ShareProvider';
import { buildSharePayload } from '@/utils/share';

interface ECardTemplate4Props {
  user: User;
  ads: AdConfig[];
  isOwnerView?: boolean;
  onAnalyticsUpdate?: (userId: string, metric: string) => void;
  onLeadSubmit?: (leadData: any) => void;
  leads?: any[];
  onContactExchange?: () => void;
}

export const ECardTemplate4: React.FC<ECardTemplate4Props> = ({ user, ads, isOwnerView = false, onAnalyticsUpdate, onLeadSubmit, leads = [], onContactExchange }) => {
  const [showQRModal, setShowQRModal] = React.useState(false);
  const [showEmailForm, setShowEmailForm] = React.useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = React.useState(false);
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const [authedLeads, setAuthedLeads] = React.useState<any[]>([]);
  const [currentAdIndex, setCurrentAdIndex] = React.useState(0);
  const { openShare } = useShare();

  // eligibility computed where needed

  const userColors = resolveUserColors(user.colors as any);

  const sourceAds = React.useMemo(() => {
    const base = Array.isArray(ads) ? [...ads] : [];
    if (base.length === 0) return mockAds;
    if (base.length >= 3) return base;
    const out = [...base];
    try {
      const mocks = (mockAds as any[]).filter((m:any) => m && m.isActive !== false);
      for (const m of mocks) {
        if (out.length >= 3) break;
        const dupe = out.find((x:any) =>
          String(x?.title || '').toLowerCase() === String(m?.title || '').toLowerCase() ||
          String(x?.link || '') === String(m?.link || '')
        );
        if (!dupe) out.push(m as any);
      }
    } catch {}
    return out;
  }, [ads]);
  const activeAds = sourceAds.filter(ad => ad.isActive);
  const currentAd = activeAds.length > 0 ? activeAds[currentAdIndex] : null;

  // Auto-scroll ads every 5 seconds
  React.useEffect(() => {
    if (activeAds.length > 1) {
      const interval = setInterval(() => {
        setCurrentAdIndex((prevIndex) => (prevIndex + 1) % activeAds.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeAds.length]);

  // Track ad view when the visible ad changes (skip mock ads without ids)
  React.useEffect(() => {
    try {
      const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId;
      if (id) {
        try { trackAdView(String(id)).catch(() => {}); } catch {}
      }
    } catch {}
  }, [currentAdIndex]);

  const handleCall = () => {
    window.open(`tel:${user.phone}`);
    // Track as dedicated callClicks for accurate reporting
    onAnalyticsUpdate?.(user.id, 'callClicks');
  };
  
  const handleEmail = () => {
    setShowEmailForm(true);
    onAnalyticsUpdate?.(user.id, 'messageClicks');
  };
  
  const handleWebsite = () => {
    window.open(user.website, '_blank');
    onAnalyticsUpdate?.(user.id, 'contactClicks');
  };
  
  const handleQR = () => {
    setShowQRModal(true);
    onAnalyticsUpdate?.(user.id, 'qrScans');
  };
  
  const handleShare = () => {
    const handle = (user as any)?.handle || (user as any)?.username || '';
    const username = (user as any)?.username || (user as any)?.id || '';
    const fallbackUrl = typeof window !== 'undefined' ? window.location.href : '';
    const cardUrl = (handle && username) ? canonicalCardUrl(handle, username) : (user.website || fallbackUrl);
    const payload = buildSharePayload({ name: user.name, url: cardUrl });
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS/i.test(navigator.userAgent||'');
    if ((navigator as any)?.share && isMobile) {
      (navigator as any).share(payload).catch(()=> openShare(cardUrl, payload.text));
    } else {
      openShare(cardUrl, payload.text);
    }
    onAnalyticsUpdate?.(user.id, 'shares');
  };
  
  const handleBroadcast = () => {
    const _exp = user.subscription?.expiresAt ? new Date(user.subscription.expiresAt as any) : null;
    const _notExpired = !_exp || _exp > new Date();
    const _subActive = (user.subscription?.isActive ?? true) && _notExpired;
    const eligible = _subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum');
    if (eligible) {
      // Eligible tiers must authenticate to verify ownership
      setShowLoginModal(true);
    } else {
      // Non-eligible: show only the soft token message; no login
      try { safeToast('info', 'This function only available for Gold or Platinum User, Subscribe Now !'); } catch {}
    }
  };

  const handleLoginSuccess = async ({ id }: { id: string }) => {
    if (String(id) !== String(user.id)) {
      safeToast('error', 'This account does not own this card.');
      return;
    }
    const _exp = user.subscription?.expiresAt ? new Date(user.subscription.expiresAt as any) : null;
    const _notExpired = !_exp || _exp > new Date();
    const _subActive = (user.subscription?.isActive ?? true) && _notExpired;
    const eligible = _subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum');
    if (!eligible) {
      safeToast('warning', 'Broadcast requires an active Gold or Platinum subscription.');
      return;
    }
    try {
      const resp: any = await listLeadsByOwner(user.id).catch(() => []);
      const items = Array.isArray(resp?.items) ? resp.items : Array.isArray(resp) ? resp : [];
      setAuthedLeads(items);
    } catch {}
    setShowBroadcastModal(true);
    onAnalyticsUpdate?.(user.id, 'messageClicks');
  };
  
  const handleSaveCard = () => {
    // Create vCard data
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${user.name}
ORG:${user.company}
TITLE:${user.position}
TEL:${user.phone}
EMAIL:${user.email}
URL:${user.website}
NOTE:${user.bio}
END:VCARD`;

    // Create blob and download
    const blob = new Blob([vCardData], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
  link.download = `${(user.name || '').replace(/\s+/g, '-')}.vcf`;
    link.click();
    URL.revokeObjectURL(url);
    
    onAnalyticsUpdate?.(user.id, 'saves');
  };
  
  const handleSocialClick = (platform: string) => {
    onAnalyticsUpdate?.(user.id, 'socialClicks');
  };

  return (
    <>
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Cover Image */}
        <div className="relative h-[200px] w-full overflow-hidden">
          <img 
            src={(user as any).coverImage || (user as any).coverUrl || DEFAULT_COVER} 
            alt="Cover" 
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 15%' }}
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              const def = String(DEFAULT_COVER);
              const remote = String(REMOTE_FALLBACK_COVER_URL);
              if (img.src !== def) {
                img.src = def;
              } else if (img.src !== remote) {
                img.src = remote;
              }
            }}
          />
        </div>

        {/* Profile Section */}
        <div className="px-6 py-6">
          {/* Profile Image with Company Badge */}
          <div className="relative -mt-16 mb-4">
            <div className="relative inline-block">
                <img 
                  src={user?.avatar ? resolveImageSrc(user.avatar) : avatarFallbackUrl(user?.name, (user as any)?.username)} 
                  alt={user.name}
                  className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow-lg"
                  style={{ marginTop: '-0.5rem' }}
                  onError={(e) => { const img = e.currentTarget as HTMLImageElement; const fb = avatarFallbackUrl(user?.name, (user as any)?.username); if (img.src !== fb) img.src = fb; }}
                />
              {/* Company Badge */}
              <div 
                className="absolute -bottom-2 -right-2 px-3 py-1 rounded-lg text-xs font-semibold text-white shadow-lg"
                style={{ backgroundColor: userColors.companyBadge }}
              >
                {(user.company || '').split(' ')[0] || 'Company'}
              </div>
            </div>
          </div>

          {/* Name and Title */}
          <div className="mb-6">
            <h1 
              className="text-2xl font-bold mb-1"
              style={{ color: userColors.text }}
            >
              {user.name}
            </h1>
            <p style={{ color: userColors.primary }}>
              {user.position} at <span style={{ color: userColors.secondary }}>{user.company}</span>
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mb-6">
            {/* First Row */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <button 
                onClick={handleCall}
                className="flex items-center justify-center gap-1.5 px-2 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors min-h-[50px]"
              >
                <Phone className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-700 leading-tight">Call</span>
              </button>
              <button 
                onClick={handleEmail}
                className="flex items-center justify-center gap-1.5 px-2 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors min-h-[50px]"
              >
                <Mail className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-700 leading-tight">Email</span>
              </button>
              <button 
                onClick={handleWebsite}
                className="flex items-center justify-center gap-1.5 px-2 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors min-h-[50px]"
              >
                <Globe className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-700 leading-tight">Website</span>
              </button>
            </div>
            {/* Second Row */}
            <div className="grid grid-cols-3 gap-3">
              <button 
                onClick={handleQR}
                className="flex items-center justify-center gap-1.5 px-2 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors min-h-[50px]"
              >
                <QrCode className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-700 leading-tight">QR Code</span>
              </button>
              <button 
                onClick={handleShare}
                className="flex items-center justify-center gap-1.5 px-2 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors min-h-[50px]"
              >
                <Share2 className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-700 leading-tight">Share</span>
              </button>
              <button 
                onClick={handleBroadcast}
                className={"flex items-center justify-center gap-1.5 px-2 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors min-h-[50px]"}
              >
                <Radio className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-700 leading-tight">Broadcast</span>
              </button>
            </div>
          </div>

          {/* Bio */}
          {user.bio && (
            <div className="mb-6">
              <div 
                className="p-4 rounded-2xl border"
                style={{ 
                  backgroundColor: userColors.background,
                  borderColor: `${userColors.primary}20`,
                  color: userColors.text
                }}
              >
                <p className="text-sm leading-relaxed">{user.bio}</p>
              </div>
            </div>
          )}

          {/* Contact and Social */}
          <div className="mb-6">
            {/* Contact Information */}
            <div className="mb-6">
              <h3 
                className="text-lg font-bold mb-4"
                style={{ color: userColors.text }}
              >
                Get In Touch
              </h3>
              <div className="space-y-4">
                {user.phone && (
                  <a
                    href={`tel:${user.phone}`}
                    className="flex items-center p-3 rounded-xl transition-all duration-200 group"
                    style={{ 
                      backgroundColor: userColors.background,
                      border: `1px solid ${userColors.primary}30`
                    }}
                    onClick={() => { try { onAnalyticsUpdate?.(user.id, 'callClicks'); } catch {} }}
                  >
                    <div 
                      className="flex items-center justify-center w-10 h-10 rounded-full text-white mr-4 group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: userColors.primary }}
                    >
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p 
                        className="text-sm font-medium"
                        style={{ color: userColors.text }}
                      >
                        Phone
                      </p>
                      <p 
                        className="text-sm"
                        style={{ color: userColors.secondary }}
                      >
                        {user.phone}
                      </p>
                    </div>
                  </a>
                )}
                {user.email && (
                  <a
                    href="#email"
                    onClick={(e)=>{ e.preventDefault(); handleEmail(); }}
                    className="flex items-center p-3 rounded-xl transition-all duration-200 group"
                    style={{ 
                      backgroundColor: userColors.background,
                      border: `1px solid ${userColors.primary}30`
                    }}
                  >
                    <div 
                      className="flex items-center justify-center w-10 h-10 rounded-full text-white mr-4 group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: userColors.primary }}
                    >
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p 
                        className="text-sm font-medium"
                        style={{ color: userColors.text }}
                      >
                        Email
                      </p>
                      <p 
                        className="text-sm"
                        style={{ color: userColors.secondary }}
                      >
                        {user.email}
                      </p>
                    </div>
                  </a>
                )}
                {user.website && (
                  <a
                    href={user.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-3 rounded-xl transition-all duration-200 group"
                    style={{ 
                      backgroundColor: userColors.background,
                      border: `1px solid ${userColors.primary}30`
                    }}
                  >
                    <div 
                      className="flex items-center justify-center w-10 h-10 rounded-full text-white mr-4 group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: userColors.primary }}
                    >
                      <Globe className="w-5 h-5" />
                    </div>
                    <div>
                      <p 
                        className="text-sm font-medium"
                        style={{ color: userColors.text }}
                      >
                        Website
                      </p>
                      <p 
                        className="text-sm"
                        style={{ color: userColors.secondary }}
                      >
                        {(user.website || '').replace(/^https?:\/\//, "")}
                      </p>
                    </div>
                  </a>
                )}
                <div 
                  className="flex items-center p-3 rounded-xl"
                  style={{ 
                    backgroundColor: userColors.background,
                    border: `1px solid ${userColors.primary}30`
                  }}
                >
                  <div 
                    className="flex items-center justify-center w-10 h-10 rounded-full text-white mr-4"
                    style={{ backgroundColor: userColors.primary }}
                  >
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p 
                      className="text-sm font-medium"
                      style={{ color: userColors.text }}
                    >
                      Location
                    </p>
                    <p 
                      className="text-sm"
                      style={{ color: userColors.secondary }}
                    >
                      Dar es Salaam, Tanzania
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="mb-8">
              <h3 
                className="text-lg font-bold mb-8"
                style={{ color: userColors.text }}
              >
                Connect With Me
              </h3>
              <div className="flex justify-start space-x-4">
                {(() => {
                  const PRIORITY = ['facebook','instagram','tiktok','twitter','linkedin'];
                  const normalize = (s: string) => String(s || '').trim().toLowerCase();
                  const alias = (p: string, url?: string) => {
                    const k = normalize(p);
                    const u = normalize(url || '');
                    if (k === 'x' || k.includes('x.com') || u.includes('x.com')) return 'twitter';
                    if (k.includes('twitter') || u.includes('twitter.com')) return 'twitter';
                    return k;
                  };
                  const ALLOWED = new Set(['facebook','instagram','tiktok','twitter','linkedin']);
                  const raw = Object.entries((user as any).socialLinks || (user as any).social || {})
                    .filter(([_, url]) => url && String(url).trim() !== '');
                  const entries = raw
                    .map(([p, u]) => [alias(String(p), String(u)), u] as [string, any])
                    .filter(([p]) => ALLOWED.has(String(p)));
                  entries.sort((a, b) => PRIORITY.indexOf(String(a[0])) - PRIORITY.indexOf(String(b[0])));
                  return entries.slice(0, 3).map(([platform, url]) => {
                    return (
                      <a 
                        key={String(platform)}
                        href={String(url)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={() => handleSocialClick(String(platform))}
                        className="p-3 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
                        style={{ backgroundColor: userColors.background }}
                      >
                        <UnifiedBrandIcon platform={String(platform)} kind="social" size={24} title={String(platform)} className="" color={userColors.accent} />
                      </a>
                    );
                  });
                })()}
              </div>
              {Object.keys((user as any).streamingLinks || {}).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries((user as any).streamingLinks || {})
                    .filter(([platform, url]) => {
                      const p = String(platform).toLowerCase();
                      const allowed = new Set(['spotify','youtube','soundcloud','boomplay','deezer','tidal','audiomack']);
                      return url && String(url).trim() !== '' && allowed.has(p);
                    })
                    .map(([platform, url]) => (
                      <a
                        key={platform}
                        href={String(url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 inline-flex items-center gap-1"
                        onClick={() => onAnalyticsUpdate?.(user.id, 'socialClicks')}
                      >
                        <UnifiedBrandIcon platform={String(platform)} kind="streaming" size={12} title={String(platform)} className="" color={userColors.accent} />
                        {platform}
                      </a>
                    ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="mb-8">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
            </div>
          </div>

          {/* Contact Exchange Button */}
          {onContactExchange && !isOwnerView && (
            <button
              onClick={onContactExchange}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 font-medium text-white mb-3 rounded-lg transition-colors shadow-lg"
              style={{ backgroundColor: userColors.primary || '#2563eb' }}
            >
              <UserPlus className="w-4 h-4" />
              Send {user.name?.split(' ')[0] || 'them'} your contact
            </button>
          )}

          {/* Save Contact Button */}
          <button 
            onClick={handleSaveCard}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 font-medium text-white mb-6 rounded-lg transition-colors shadow-lg"
            style={{ backgroundColor: userColors.saveButton }}
          >
            <Download className="w-4 h-4" />
            Save Contact
          </button>

          {/* Advertisement Space */}
          <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
            <div className="text-center relative overflow-hidden">
              <p className="text-xs text-gray-500 mb-4 uppercase tracking-wide font-medium">
                SPONSORED
              </p>
              {currentAd ? (
                <div className="relative">
                  <a 
                    href={currentAd.link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="block transition-all duration-500 ease-in-out"
                    key={currentAdIndex}
                    onClick={() => { try { const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId; if (id) { try { trackAdClick(String(id)).catch(() => {}); } catch {} } } catch {} }}
                  >
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-gray-400 transition-all duration-500 bg-white" style={{ minHeight: 170 }}>
                      <img 
                        src={currentAd.image} 
                        alt={currentAd.title}
                        className="w-full h-20 object-cover rounded mb-3 transition-opacity duration-500"
                      />
                      <h4 className="font-semibold text-gray-800 mb-1 transition-opacity duration-500 truncate" title={currentAd.title}>{currentAd.title}</h4>
                      <p className="text-sm text-gray-600 transition-opacity duration-500 line-clamp-2" title={currentAd.description}>{currentAd.description}</p>
                    </div>
                  </a>
                  
                  {/* Ad indicators */}
                    {activeAds.length > 1 && (
                      <div className="flex justify-center mt-3 space-x-2">
                        {activeAds.map((_, index) => (
                          <div
                            key={index}
                            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                              index === currentAdIndex ? 'bg-gray-600' : 'bg-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-gray-400 bg-white">
                  <div className="text-lg font-medium mb-2 text-gray-300">Advertisement Space</div>
                  <div className="text-sm text-gray-400">Your ad could be here</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        user={user}
      />

      <EnhancedEmailLeadForm
        isOpen={showEmailForm}
        onClose={() => setShowEmailForm(false)}
        recipientName={user.name}
        recipientEmail={user.email}
        cardOwnerId={user.id}
        onLeadSubmit={onLeadSubmit}
        onAnalyticsUpdate={() => onAnalyticsUpdate?.(user.id, 'formSubmissions')}
      />

      <BroadcastModal
        isOpen={showBroadcastModal}
        onClose={() => setShowBroadcastModal(false)}
        userName={user.name}
        cardOwnerId={user.id}
        leads={authedLeads.length ? authedLeads : leads}
      />

      <BroadcastLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
      />
  {/* share popup handled by global ShareProvider */}
    </>
  );
};